import * as React from "react";
import PropTypes from "prop-types";
import Loading from "../Loading";
import { useNavigate } from "react-router-dom";
import { usePrimaryColor } from "./PrimaryColorContext";


/**
 * TabPanel - renders content when the tab is active
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {number} props.index
 * @param {number} props.value
 * @returns {JSX.Element}
 * @author Forge
 * @version 2.0.0
 * @since 2025-04-11
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      className="flex-grow ht-tab-fade-in"
      {...other}
    >
      {value === index && (
        <div className="p-5">
          {children}
        </div>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

/**
 * HtTabs - Premium segmented tab control with animated pill slider
 * @param {object} props
 * @param {string[]} props.labels - Tab labels
 * @param {React.ReactNode[]} props.components - Tab content components
 * @param {React.ReactNode} [props.nextToTabs] - Content placed to the right of tabs
 * @param {string} [props.localStorage] - Key to persist active tab in localStorage
 * @param {object} [props.style] - Inline styles for the container
 * @param {string} [props.variant] - "fullWidth" for equal-width tabs
 * @param {function} [props.updateValue] - Callback when tab changes
 * @param {boolean} [props.loading] - Show loading state
 * @param {function} [props.setLocation] - Callback with new tab index
 * @param {number} [props.defaultValue] - Default active tab index
 * @param {string} [props.navigate] - Route pattern with ":page" placeholder
 * @returns {JSX.Element}
 * @author Forge
 * @version 2.0.0
 * @since 2025-04-11
 */
export default function HtTabs(props) {
  const {
    labels,
    components,
    nextToTabs,
    localStorage: lsKey,
    style = {},
    variant,
    updateValue,
    loading,
    setLocation,
    defaultValue,
    navigate,
  } = props;

  const [value, setValue] = React.useState(() => {
    if (defaultValue !== undefined) return defaultValue;
    if (!lsKey) return 0;
    let ls = getLocalStorage(lsKey);
    if (ls !== undefined) return ls;
    return 0;
  });

  const nav = useNavigate();
  const tabsRef = React.useRef(null);
  const [sliderStyle, setSliderStyle] = React.useState({
    left: 0,
    width: 0,
    opacity: 0,
  });
  const { primaryColor } = usePrimaryColor();

  React.useEffect(() => {
    if (tabsRef.current) {
      const activeTab = tabsRef.current.querySelector("[data-active='true']");
      if (activeTab) {
        setSliderStyle({
          left: activeTab.offsetLeft,
          width: activeTab.offsetWidth,
          opacity: 1,
        });
      }
    }
  }, [value, labels]);

  const handleChange = (newValue) => {
    setValue(newValue);
    if (lsKey) setLocalStorage(lsKey, newValue);
    if (setLocation) setLocation(newValue);
    if (updateValue) updateValue(newValue);
    if (navigate && labels[newValue]) {
      nav(navigate.replace(":page", labels[newValue].toLowerCase()));
    }
  };

  React.useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== value) {
      setValue(defaultValue);
    }
  }, [defaultValue]);

  const isFullWidth = variant === "fullWidth";

  return (
    <div className="flex flex-col w-full" style={{ padding: 0, ...style }}>
      <style>{`
        @keyframes htTabFadeIn {
          from {
            opacity: 0;
            transform: translateY(1px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .ht-tab-fade-in {
          animation: htTabFadeIn 200ms ease-out;
        }
      `}</style>

      <header className="flex justify-between items-center pb-2 mb-2 box-border">
        <nav
          className={`relative overflow-x-auto scrollbar-none bg-slate-100 rounded-2xl p-1.5 border border-slate-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.03)] gap-0.5 ${isFullWidth ? "w-full" : "flex"}`}
          role="tablist"
          ref={tabsRef}
        >
          {labels.map((l, i) => (
            <button
              key={"tablabel" + i}
              {...a11yProps(i)}
              data-active={value === i}
              className={`relative z-10 bg-transparent border-none px-5 py-2 font-semibold text-[0.82rem] whitespace-nowrap select-none outline-none cursor-pointer transition-colors duration-150 ease-out rounded-xl ${
                isFullWidth ? "flex-1 text-center" : ""
              } ${
                value === i
                  ? `text-${primaryColor}-800`
                  : "text-slate-500 hover:text-slate-800 hover:bg-white/60"
              }`}
              role="tab"
              aria-selected={value === i}
              onClick={() => handleChange(i)}
              type="button"
            >
              {l}
            </button>
          ))}
          <div
            className="absolute top-1.5 bottom-1.5 left-0 rounded-xl bg-white pointer-events-none z-0"
            style={{
              transform: `translateX(${sliderStyle.left}px)`,
              width: `${sliderStyle.width}px`,
              opacity: sliderStyle.opacity,
              boxShadow:
                "0 1px 3px rgba(0,0,0,0.07), 0 4px 12px rgba(0,0,0,0.04)",
              border: "1px solid rgba(226,232,240,0.8)",
              transition:
                "transform 400ms cubic-bezier(0.32, 0.72, 0, 1), width 400ms cubic-bezier(0.32, 0.72, 0, 1), opacity 200ms ease-out",
            }}
          />
        </nav>
        {nextToTabs && (
          <div className="flex items-center ml-4">
            {nextToTabs}
          </div>
        )}
      </header>

      <DrawComponents
        components={components}
        loading={loading}
        value={value}
      />
    </div>
  );
}

/**
 * DrawComponents - renders the active tab panel
 * @param {object} props
 * @param {React.ReactNode[]} props.components
 * @param {boolean} props.loading
 * @param {number} props.value
 * @returns {React.ReactNode}
 * @author Forge
 * @version 2.0.0
 */
function DrawComponents({ components, loading, value }) {
  if (loading) return <Loading />;
  if (!components) return null;
  return components.map((c, i) => (
    <TabPanel
      value={value}
      index={i}
      key={"tab" + i}
      style={{ height: "100%", flexGrow: 1 }}
    >
      {c}
    </TabPanel>
  ));
}

function getLocalStorage(name) {
  try {
    const parsed = parseInt(window.localStorage.getItem(name));
    return isNaN(parsed) ? undefined : parsed;
  } catch {
    return undefined;
  }
}

function setLocalStorage(name, val) {
  try {
    window.localStorage.setItem(name, val);
  } catch {
    // silently fail
  }
}
